/**
 * AI tutor (Gemini REST API) request/response handling, kept free of fetch so it can be
 * tested. The network call lives in src/data/gemini.ts.
 */

/** Tried in order; later models are used when an earlier one is unavailable or out of quota. */
/**
 * Tried in order. Gemini 2.0 was shut down in June 2026 and 2.5 is served only to keys that used it
 * before, so current models come first and 2.5 stays as a last resort for older keys.
 */
export const GEMINI_MODELS = ['gemini-3.5-flash-lite', 'gemini-3.6-flash', 'gemini-3.8-flash', 'gemini-2.5-flash'] as const;

/** A model that has not answered within this time is skipped for the next one. */
export const GEMINI_TIMEOUT_MS = 20_000;

export type ThinkingConfig = { thinkingBudget: number } | { thinkingLevel: 'minimal' | 'low' };

/**
 * Keeps hidden "thinking" to a minimum: it is slow and counts against maxOutputTokens, so a
 * long think can leave no visible answer. 2.5 takes a budget, 3.x a level (3.8 has no
 * 'minimal').
 */
export function thinkingConfigFor(model: string): ThinkingConfig | undefined {
  if (model.startsWith('gemini-2.5')) return { thinkingBudget: 0 };
  if (model.startsWith('gemini-3.8')) return { thinkingLevel: 'low' };
  if (model.startsWith('gemini-3')) return { thinkingLevel: 'minimal' };
  return undefined;
}
export const GEMINI_ENDPOINT = 'https://generativelanguage.googleapis.com/v1beta/models';
export const GEMINI_KEY_URL = 'https://aistudio.google.com/apikey';
/** Messages of history sent with each request, to keep requests small. */
export const HISTORY_LIMIT = 12;

export interface ChatMessage {
  role: 'user' | 'tutor';
  text: string;
}

export interface TutorContext {
  languageRu: string;
  lessonTitle?: string;
  sentence?: { text: string; pinyin?: string; ru: string };
  grammar?: string[];
  words?: string[];
}

export function systemPrompt(ctx: TutorContext): string {
  const lines = [
    `Ты — Робо-кот, терпеливый учитель (${ctx.languageRu} язык) в личном приложении «Сөйле».`,
    'Отвечай по-русски, коротко: 2–5 предложений. Примеры на изучаемом языке давай с переводом; для китайского — с пиньинем.',
    'Объясняй через примеры из текущего урока. Если ученик ошибся, мягко покажи правильный вариант и почему.',
    'Никогда не давай готовых ответов на упражнения, тесты и экзаменационные задания: дай подсказку или наводящий вопрос.',
    'Если вопрос не про язык, вежливо верни разговор к уроку.',
  ];
  if (ctx.lessonTitle) lines.push(`Текущий урок: ${ctx.lessonTitle}.`);
  if (ctx.sentence) {
    const pinyin = ctx.sentence.pinyin ? ` (${ctx.sentence.pinyin})` : '';
    lines.push(`Ученик сейчас читает предложение: ${ctx.sentence.text}${pinyin} — «${ctx.sentence.ru}».`);
  }
  if (ctx.grammar && ctx.grammar.length > 0) lines.push(`Грамматика урока: ${ctx.grammar.join(', ')}.`);
  if (ctx.words && ctx.words.length > 0) lines.push(`Новые слова урока: ${ctx.words.join(', ')}.`);
  return lines.join('\n');
}

interface GeminiPart {
  text: string;
}
interface GeminiContent {
  role: 'user' | 'model';
  parts: GeminiPart[];
}
export interface GeminiRequest {
  systemInstruction: { parts: GeminiPart[] };
  contents: GeminiContent[];
  generationConfig: {
    temperature: number;
    maxOutputTokens: number;
    thinkingConfig?: ThinkingConfig;
  };
}

export function buildRequest(
  model: string,
  ctx: TutorContext,
  history: readonly ChatMessage[],
): GeminiRequest {
  const recent = history.slice(-HISTORY_LIMIT);
  // Gemini requires the conversation to start with a user turn.
  const firstUser = recent.findIndex((m) => m.role === 'user');
  const contents = (firstUser === -1 ? [] : recent.slice(firstUser)).map(
    (m): GeminiContent => ({ role: m.role === 'user' ? 'user' : 'model', parts: [{ text: m.text }] }),
  );
  return {
    systemInstruction: { parts: [{ text: systemPrompt(ctx) }] },
    contents,
    generationConfig: {
      temperature: 0.6,
      maxOutputTokens: 2048,
      ...withThinking(model),
    },
  };
}

/** Spread into generationConfig: the model's thinking setting, or nothing. */
export function withThinking(model: string): { thinkingConfig?: ThinkingConfig } {
  const config = thinkingConfigFor(model);
  return config ? { thinkingConfig: config } : {};
}

export type TutorErrorCode = 'no_key' | 'bad_key' | 'quota' | 'offline' | 'blocked' | 'unavailable' | 'unknown';

export class TutorError extends Error {
  readonly code: TutorErrorCode;
  constructor(code: TutorErrorCode) {
    super(TUTOR_ERROR_TEXT[code]);
    this.code = code;
    this.name = 'TutorError';
  }
}

export const TUTOR_ERROR_TEXT: Record<TutorErrorCode, string> = {
  no_key: 'Чтобы поговорить с учителем, добавьте свой ключ Gemini в Профиле → «ИИ (Gemini)».',
  bad_key: 'Ключ Gemini не подходит. Проверьте его в Профиле → «ИИ (Gemini)».',
  quota: 'Бесплатный лимит запросов Gemini на сейчас закончился. Попробуйте через несколько минут.',
  offline: 'Нет интернета. Учитель ответит, когда связь вернётся.',
  blocked: 'Учитель не может ответить на этот вопрос. Попробуйте спросить иначе.',
  unavailable: 'ИИ Gemini сейчас недоступен (перегружен или модель отключена). Попробуйте чуть позже.',
  unknown: 'Что-то пошло не так. Попробуйте ещё раз.',
};

/** Maps an HTTP status to an error code; null for 2xx. */
export function errorFromStatus(status: number, body: string): TutorErrorCode | null {
  if (status >= 200 && status < 300) return null;
  if (status === 429) return 'quota';
  if (status === 401) return 'bad_key';
  // 403 is also returned for models a key may not use (e.g. legacy ones): only an API-key
  // complaint means the key itself is wrong; otherwise try the next model.
  if (status === 403) return /API[_ ]?KEY/i.test(body) ? 'bad_key' : 'unavailable';
  if (status === 400 && /API_KEY_INVALID|API key not valid/i.test(body)) return 'bad_key';
  if (status === 404 || status === 500 || status === 503) return 'unavailable';
  return 'unknown';
}

/** Whether a failure with this code should be retried on the next model in GEMINI_MODELS. */
export function shouldTryNextModel(code: TutorErrorCode): boolean {
  return code === 'unavailable' || code === 'quota';
}

interface GeminiResponse {
  candidates?: { content?: { parts?: { text?: string }[] }; finishReason?: string }[];
  promptFeedback?: { blockReason?: string };
}

export function parseResponse(json: unknown): string {
  const res = json as GeminiResponse;
  if (res.promptFeedback?.blockReason) throw new TutorError('blocked');
  const candidate = res.candidates?.[0];
  const text = (candidate?.content?.parts ?? [])
    .map((p) => p.text ?? '')
    .join('')
    .trim();
  if (!text) throw new TutorError(candidate?.finishReason === 'SAFETY' ? 'blocked' : 'unavailable');
  return text;
}

/** Single-history key used before chats were split by language. */
export const LEGACY_CHAT_HISTORY_KEY = 'soile.chat.v1';
/** Languages that may have a stored tutor chat (cleared on sign-out). */
export const CHAT_LANGS = ['zh', 'en', 'tr'] as const;

/** Each language has its own tutor conversation on the device. */
export function chatHistoryKey(lang: string): string {
  return `${LEGACY_CHAT_HISTORY_KEY}.${lang}`;
}

/**
 * The old single history was always Chinese (the only course with a tutor then): move it
 * to the Chinese key unless that already exists, and drop the old key.
 */
export function migrateLegacyChat(
  read: (key: string) => string | null,
  write: (key: string, value: string | null) => void,
): void {
  const legacy = read(LEGACY_CHAT_HISTORY_KEY);
  if (legacy === null) return;
  if (read(chatHistoryKey('zh')) === null) write(chatHistoryKey('zh'), legacy);
  write(LEGACY_CHAT_HISTORY_KEY, null);
}
